import React, { useEffect, useState, useRef } from "react";
import HistoryPanel from "@/components/common/HistoryPanel";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, GripVertical, MapPin, Calendar, ListChecks, FileText, Folder, AlertTriangle, ArrowRight, ArrowRightLeft, RefreshCw, FilePlus2, ChevronDown, ChevronRight, Users, Bookmark, Settings, Lock, Link as LinkIcon } from "lucide-react";
import { OutlookTabs } from "@/components/common/OutlookTabs";
import { toast } from "@/lib/toast";
import StepResponsibleSelect from "@/components/documents/StepResponsibleSelect";
import AutoTaskSection from "@/components/documents/AutoTaskSection";
import api from "@/lib/api";
import UserAvatar from "@/components/ui/UserAvatar";
import { deriveShortName, SHORT_NAME_MAX } from "@/lib/shortName";
import ManageMemoryValuesDialog from "@/components/documents/ManageMemoryValuesDialog";
import { PropagationChoiceDialog, ModeChangeConfirmDialog, ResponsablesPropagationDialog } from "@/components/documents/DocumentTypeDialogSubs";
import { MemoryCountPastille, FieldRow, StepCard, DeadlineSentence, ToggleSwitch } from "@/components/documents/DocumentTypeDialogParts";
import ConfirmDeleteStepDialog from "@/components/common/ConfirmDeleteStepDialog";
import { REQUIRED_FIELD_TYPES, DOC_FIELD_TYPES, CREATION_ANCHOR, FRESH_MS } from "@/components/documents/DocumentTypeDialogConsts";
import { DSelect } from "@/components/common/DeskSelect";

// Pinned "Titre" field — locked at position 0 of every DocumentType.
// The admin cannot rename it, retype it, deactivate it or delete it. See
// /app/memory/project_rules_spec.md §7. Backend also runs a startup
// migration to prepend this field on legacy types.
const PINNED_TITRE_KEY = "titre";
const buildPinnedTitreField = () => ({
  id: `pinned-titre-${Date.now()}`,
  key: PINNED_TITRE_KEY,
  label: "Titre",
  type: "text",
  required: true,
  order: 0,
  options: [],
});


// Spec 2026-02-XX rev22 (Point Q) / rev24 (Point S) — `deriveShortName`
// et `SHORT_NAME_MAX` sont désormais dans `@/lib/shortName` pour
// mutualisation avec `DocumentTypesAdmin.jsx` (bouton Regénérer auto).

// slugify → machine key for DocumentField
const _slug = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")   // strip diacritics
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || "champ";

/**
 * DocumentTypeDialog — admin CRUD for DocumentTypes.
 *
 * v3 layout (spec `Refonte builder d'étapes — Fusion + formulation phrase`):
 *   • Type-level custom fields are gone — each step carries at most ONE
 *     required field, and that's the only "value" surface.
 *   • Single list of workflow steps, one "+ Ajouter une étape" button
 *     top-right (no duplicate at bottom).
 *   • Drag & drop reordering only (HTML5 native, ⠿ handle).
 *   • Deadline configured as a sentence:
 *       "[N] jours après l'étape [01|02|...]"      (step > 1)
 *       "[N] jours après création du document"     (step 1)
 *     — anchor is chosen via clickable numbered pills, not a dropdown.
 *   • Delete button uses filled red #EF4444 with hover-tinted background.
 */

/**
 * NextColorPreview — spec 2026-02-XX rev16 (point I).
 * Affiche une mini-preview du badge avec la couleur qui sera assignée
 * automatiquement. La couleur est FETCHÉE UNE FOIS au montage (elle
 * dépend uniquement de l'ensemble des types existants, pas du nom).
 * Le nom saisi apparaît dans le badge en temps réel via `name` prop.
 */
function NextColorPreview({ name, shortName }) {
  const [color, setColor] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/document-types/next-color");
        if (!cancelled) setColor(data?.color || "#EF4444");
      } catch {
        if (!cancelled) setColor("#EF4444");
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // rev22 — l'aperçu utilise le nom court (ce qui sera affiché dans le tableau)
  const label = (shortName || "").trim() || (name || "").trim() || "Nouveau type";
  return (
    <div className="flex items-center gap-2" data-testid="doctype-color-preview">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
        Aperçu badge
      </span>
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap"
        style={{
          background: `${color || "#666"}22`,
          color: color || "#888",
          border: `1px solid ${color || "#666"}55`,
          minWidth: 0,
          maxWidth: 220,
        }}
        title={`${label} · couleur auto ${color || "en cours…"}`}
      >
        <span
          className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: color || "#666" }}
        />
        <span className="truncate">{label}</span>
      </span>
      <span className="text-[10px] text-muted-foreground/70 italic">
        {color ? "Auto-assigné — distinct + lisible sur fond sombre" : "Chargement…"}
      </span>
    </div>
  );
}


// iter144 — 3 modes : Avec étapes (sequential/parallel) · Sans étape (simple) · Dossier (ambre).
const MODE_CARDS = [
  { key: "sequential", label: "Avec étapes", hint: "Workflow multi-étapes avec suivi de progression", Icon: ListChecks },
  { key: "simple",     label: "Sans étape",  hint: "Juste les champs et pièces jointes à scanner",     Icon: FileText },
  { key: "dossier",    label: "Dossier",     hint: "Évolutif — socle obligatoire, ajout de champs et d'étapes à la volée", Icon: Folder },
];
const modeGroup = (m) => (m === "simple" ? "simple" : m === "dossier" ? "dossier" : "sequential");
const modeCardStyle = (key, active) => {
  if (!active) return { background: "#0f0f12", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "none", icon: { background: "rgba(255,255,255,0.04)", color: "#9CA3AF" }, label: "#D1D5DB", hint: "#8B8F94" };
  if (key === "dossier") return { background: "linear-gradient(135deg, rgba(217,155,30,.12), transparent)", border: "1px solid #D99B1E", boxShadow: "none", icon: { background: "rgba(217,155,30,.25)", color: "#e6c383" }, label: "#e6c383", hint: "#B0B3B8" };
  return { background: "rgba(0,120,212,0.08)", border: "2px solid #0078D4", boxShadow: "0 2px 12px rgba(0,120,212,0.15)", icon: { background: "rgba(0,120,212,0.18)", color: "#0078D4" }, label: "#F2F2F2", hint: "#B0B3B8" };
};

export default function DocumentTypeDialog({ open, onOpenChange, editing, onSaved }) {
  const isEdit = !!editing;
  const [name, setName] = useState("");
  // Spec 2026-02-XX rev22 (Point Q) — nom court obligatoire (≤ 20 chars)
  // affiché dans les badges du tableau Documents + filtres. Le nom complet
  // reste utilisé dans la fiche + les exports PDF. Auto-préfillé depuis
  // ``deriveShortName(name)`` en mode CREATE tant que l'utilisateur n'a
  // pas touché manuellement le champ — dès la première modif, le lien
  // auto se désactive (``shortNameTouched``).
  const [shortName, setShortName] = useState("");
  const [shortNameTouched, setShortNameTouched] = useState(false);
  const [locationRequired, setLocationRequired] = useState(true);
  const [lotLinked, setLotLinked] = useState(false); // iter359 pt.7
  const [autoTaskEnabled, setAutoTaskEnabled] = useState(false); // iter143
  const [autoTaskAssignees, setAutoTaskAssignees] = useState([]);
  const [fields, setFields] = useState([]);
  const [steps, setSteps] = useState([]);
  const [freshStepIds, setFreshStepIds] = useState(() => new Set());
  // Spec iter211 (2026-02-28) — confirmation avant suppression d'étape.
  // Historique : le bouton Trash2 rouge sur chaque StepCard appelait
  // rmStep() immédiatement, sans confirmation ni rappel du fait que
  // le créateur sera notifié. On passe désormais par une modale dédiée.
  const [stepToDelete, setStepToDelete] = useState(null); // {idx, label}
  const [saving, setSaving] = useState(false);

  // HTML5 drag state (steps).
  const dragFromRef = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  // Separate drag state for the "Champs du document" list.
  const dragFieldFromRef = useRef(null);
  const [dragFieldOverIdx, setDragFieldOverIdx] = useState(null);
  const [workflowMode, setWorkflowMode] = useState("sequential");
  // Spec rev147 (2026-02-07) — Le mode workflow est désormais éditable
  // après création via un bouton dédié « Changer le mode ». Un état
  // séparé garde le mode d'origine (à l'ouverture du dialog) pour :
  //   1) détecter la transition et afficher les bons messages,
  //   2) permettre à l'admin d'annuler et revenir en arrière tant
  //      qu'il n'a pas sauvegardé.
  const [originalWorkflowMode, setOriginalWorkflowMode] = useState("sequential");
  const [modeUnlocked, setModeUnlocked] = useState(false);
  const [modeChangeConfirmOpen, setModeChangeConfirmOpen] = useState(false);
  const [modeChangeConfirmTarget, setModeChangeConfirmTarget] = useState(null); // "simple" | "stepped"
  // Spec 2026-02-XX rev10 (Feb 2026) — couleur du badge de type. Éditable
  // manuellement par l'admin ; auto-assignée à la création si laissée vide.
  const [color, setColor] = useState("");
  // Spec rev75 (2026-08-02) — catégorie transversale optionnelle.
  const [categoryId, setCategoryId] = useState("");           // id ou "" (aucune)
  const [categories, setCategories] = useState([]);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryColor, setNewCategoryColor] = useState(null);
  const [savingCategory, setSavingCategory] = useState(false);
  // Spec iter121 pt.4 (Feb 2026) — liste de responsables au niveau TYPE
  // (indépendante des étapes). Symétrique à ``workflow_steps[].responsible_user_ids``.
  const [typeResponsibleIds, setTypeResponsibleIds] = useState([]);

  // Spec rev117 (2026-02) — Tabs (Configuration / Historique) + bannière
  // d'alerte de propagation + fetch du journal.
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("config");
  // Bannière d'impact : { changes: [], affected_docs_count: N } — populé
  // par l'endpoint /impact-preview quand steps changent (édition
  // uniquement, debounced 400ms).
  const [impactPreview, setImpactPreview] = useState(null);
  // Spec rev134 (2026-02-06) — état de la modale de confirmation de
  // propagation (« Propager » vs « Futurs seulement »).
  const [propagationDialogOpen, setPropagationDialogOpen] = useState(false);
  const [pendingSavePayload, setPendingSavePayload] = useState(null);
  // Spec iter118b (Feb 2026) — modale dédiée pour la propagation des
  // MULTI-RESPONSABLES (chips + accordéon liste des docs impactés).
  // Se déclenche UNIQUEMENT quand le seul changement structurel est
  // ``responsibles_changed`` (sinon on garde la modale rev134 générique
  // qui propage l'ensemble des workflow_steps, responsables inclus).
  const [respPropagationDialogOpen, setRespPropagationDialogOpen] = useState(false);
  const [respPropagationChanges, setRespPropagationChanges] = useState([]);
  const [impactLoading, setImpactLoading] = useState(false);
  // Journal : lazy-loaded quand l'onglet "Historique" est ouvert.
  const [historyItems, setHistoryItems] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Spec iter212 (2026-02-28) — filtre local sur les entrées d'historique.
  const [historyFilter, setHistoryFilter] = useState("all");

  // Charge la liste des catégories + preview de la couleur pour une
  // nouvelle catégorie à chaque ouverture ou passage en mode création.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/document-type-categories");
        if (!cancelled) setCategories(data || []);
      } catch { /* silencieux — non-bloquant */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!creatingCategory) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/document-type-categories/next-color");
        if (!cancelled) setNewCategoryColor(data?.color || "#5A9EE0");
      } catch { /* silencieux */ }
    })();
    return () => { cancelled = true; };
  }, [creatingCategory]);

  useEffect(() => {
    if (open) {
      setName(editing?.name || "");
      setShortName(editing?.short_name || (editing ? deriveShortName(editing.name || "") : ""));
      setShortNameTouched(!!editing);  // en édition, on ne veut PAS d'auto-lien
      setColor(editing?.color || "");
      setCategoryId(editing?.category_id || "");
      setCreatingCategory(false);
      setNewCategoryName("");
      setNewCategoryColor(null);
      setLocationRequired(editing?.location_required ?? true);
      setLotLinked(!!editing?.lot_linked);
      setAutoTaskEnabled(!!editing?.auto_task_enabled);
      setAutoTaskAssignees(Array.isArray(editing?.auto_task_assignees) ? editing.auto_task_assignees.filter((a) => a?.user_id) : []);
      // Spec iter121 pt.4 (Feb 2026) — reset responsables niveau type.
      setTypeResponsibleIds(
        Array.isArray(editing?.responsible_user_ids)
          ? editing.responsible_user_ids.filter(Boolean)
          : []
      );
      setWorkflowMode(editing?.workflow_mode || "sequential");
      // Spec rev147 — reset des états liés au changement de mode.
      setOriginalWorkflowMode(editing?.workflow_mode || "sequential");
      setModeUnlocked(false);
      setModeChangeConfirmOpen(false);
      setModeChangeConfirmTarget(null);
      // Ensure the pinned "Titre" field always sits at position 0 — even
      // when opening a legacy type that predates this rule (backend
      // migration should already have covered it, but we double-defend on
      // the client so the toggle/label/type stay locked).
      const existing = (editing?.fields || []);
      const hasPinned = existing.some((f) => (f.key || "").toLowerCase() === PINNED_TITRE_KEY);
      const merged = hasPinned
        ? existing
        : [buildPinnedTitreField(), ...existing];
      setFields(merged.map((f, i) => ({ ...f, order: i + 1 })));
      setSteps((editing?.workflow_steps || []).map((s, i) => {
        // Ensure the step always has a `required_fields` array populated
        // — the backend always sends one now, but defend against older
        // payloads by synthesising one from the legacy singleton.
        let rf = Array.isArray(s.required_fields) ? [...s.required_fields] : [];
        if (rf.length === 0) {
          rf = [{
            id: `legacy-rf-${s.id || i}`,
            label: (s.label || "Champ requis"),
            type: s.required_field_type || "text",
            options: s.required_field_options || [],
            active: !!s.required_field_active,
          }];
        }
        // Spec iter118a (Feb 2026) — normalise en LISTE de responsables
        // (accepte les anciens payloads singleton pour compat lecture).
        const rids = Array.isArray(s.responsible_user_ids)
          ? s.responsible_user_ids.filter(Boolean)
          : (s.responsible_user_id ? [s.responsible_user_id] : []);
        return { ...s, required_fields: rf, responsible_user_ids: rids, order: i + 1 };
      }));
      setFreshStepIds(new Set());
      // Reset tab & impact banner + journal state on every open
      setActiveTab("config");
      setImpactPreview(null);
      setHistoryItems(null);
    }
  }, [open, editing]);

  // Spec rev117 — Debounced fetch de l'impact preview. Se déclenche
  // quand steps changent en mode ÉDITION uniquement (aucun sens à la
  // création d'un nouveau type — pas encore de documents attachés).
  useEffect(() => {
    if (!open || !isEdit) return;
    if (activeTab !== "config") return;
    const controller = new AbortController();
    const handle = setTimeout(async () => {
      try {
        setImpactLoading(true);
        // Réutilise la même normalisation que submit() pour envoyer un
        // payload cohérent avec ce qui serait persisté.
        const stepIdByIdx = steps.map((s, i) => s.id || `pending-preview-${i}`);
        const wf = steps.map((s, i) => {
          let fromId = s.deadline_from_step_id;
          if (fromId && fromId !== CREATION_ANCHOR && String(fromId).startsWith("__idx__:")) {
            const j = parseInt(String(fromId).split(":")[1], 10);
            fromId = stepIdByIdx[j];
          }
          const cleanedRF = (s.required_fields || []).map((f, k) => ({
            id: f.id && !String(f.id).startsWith("new-") ? f.id : undefined,
            label: (f.label || `Champ ${k + 1}`).trim() || `Champ ${k + 1}`,
            type: f.type || "text",
            options: f.type === "dropdown"
              ? (f.options || []).map((o) => String(o).trim()).filter(Boolean)
              : [],
            active: f.active !== false,
          }));
          return {
            id: stepIdByIdx[i],
            label: (s.label || "").trim(),
            order: i + 1,
            required_fields: cleanedRF,
            // Spec iter118a — LISTE de responsables (multi).
            responsible_user_ids: Array.isArray(s.responsible_user_ids)
              ? s.responsible_user_ids.filter(Boolean)
              : (s.responsible_user_id ? [s.responsible_user_id] : []),
            deadline_required: !!s.deadline_required,
            deadline_mode: s.deadline_required ? (s.deadline_mode || "from_step") : null,
            deadline_from_step_id: s.deadline_required && s.deadline_mode === "from_step" ? fromId : null,
            deadline_days: s.deadline_required && s.deadline_mode === "from_step" ? Number(s.deadline_days) || 0 : 0,
            deadline_fixed: s.deadline_required && s.deadline_mode === "fixed" ? (s.deadline_fixed || null) : null,
          };
        });
        // Spec rev146 (2026-02-06) — on envoie aussi les fields
        // (bloc CHAMPS DU DOCUMENT) pour que le backend puisse détecter
        // les changements structurels au niveau des champs généraux
        // (ajout/retrait/toggle required/type/label/options).
        const cleanedFields = (fields || []).map((f, k) => ({
          id: f.id && !String(f.id).startsWith("new-") ? f.id : undefined,
          key: f.key || "",
          label: (f.label || `Champ ${k + 1}`).trim() || `Champ ${k + 1}`,
          type: f.type || "text",
          required: !!f.required,
          options: f.type === "dropdown"
            ? (f.options || []).map((o) => String(o).trim()).filter(Boolean)
            : [],
          order: k,
          full_width: !!f.full_width,
          help_text: f.help_text || null,
        }));
        const { data } = await api.post(
          `/document-types/${editing.id}/impact-preview`,
          { workflow_steps: wf, fields: cleanedFields, workflow_mode: workflowMode },
          { signal: controller.signal },
        );
        setImpactPreview(data || null);
      } catch (err) {
        if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
          // silencieux — on n'affiche pas d'erreur à l'admin pour un
          // simple preview, la bannière disparaît c'est tout.
        }
      } finally {
        setImpactLoading(false);
      }
    }, 400);
    return () => { clearTimeout(handle); controller.abort(); };
  }, [steps, fields, open, isEdit, editing?.id, activeTab, workflowMode]);

  // Spec rev117 — Lazy-load du journal quand l'onglet Historique s'ouvre.
  useEffect(() => {
    if (!open || !isEdit) return;
    if (activeTab !== "history") return;
    if (historyItems !== null) return;  // déjà chargé
    let cancelled = false;
    (async () => {
      try {
        setHistoryLoading(true);
        const { data } = await api.get(`/document-types/${editing.id}/history`);
        if (!cancelled) setHistoryItems(data?.items || []);
      } catch {
        if (!cancelled) setHistoryItems([]);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isEdit, editing?.id, activeTab, historyItems]);

  // ---- Document fields handlers -------------------------------------------
  const addField = () => {
    setFields((prev) => [...prev, {
      id: `new-${Date.now()}-${prev.length}`,
      key: "",
      label: "",
      type: "text",
      required: false,
      order: prev.length + 1,
    }]);
  };
  const updField = (idx, patch) =>
    setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const rmField = (idx) => {
    // The pinned "Titre" field (index 0, key=PINNED_TITRE_KEY) is not
    // removable — silently ignore attempts.
    setFields((prev) => {
      const target = prev[idx];
      if (target && (target.key || "").toLowerCase() === PINNED_TITRE_KEY) return prev;
      return prev.filter((_, i) => i !== idx).map((f, i) => ({ ...f, order: i + 1 }));
    });
  };

  const onFieldDragStart = (i) => (e) => {
    dragFieldFromRef.current = i;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", `field:${i}`); } catch (_) { /* Firefox */ }
  };
  const onFieldDragOver = (i) => (e) => {
    e.preventDefault(); e.dataTransfer.dropEffect = "move";
    if (dragFieldOverIdx !== i) setDragFieldOverIdx(i);
  };
  const onFieldDrop = (targetIdx) => (e) => {
    e.preventDefault();
    const from = dragFieldFromRef.current;
    dragFieldFromRef.current = null; setDragFieldOverIdx(null);
    if (from === null || from === targetIdx) return;
    setFields((prev) => {
      // The pinned "Titre" field must stay at index 0. Reject moves that
      // (a) start from it or (b) target index 0.
      const source = prev[from];
      if (source && (source.key || "").toLowerCase() === PINNED_TITRE_KEY) return prev;
      if (targetIdx === 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(targetIdx, 0, moved);
      return next.map((f, i) => ({ ...f, order: i + 1 }));
    });
  };
  const onFieldDragEnd = () => { dragFieldFromRef.current = null; setDragFieldOverIdx(null); };

  // ---- Workflow steps handlers ---------------------------------------------
  const addStep = () => {
    const newId = `new-${Date.now()}-${steps.length}`;
    setSteps((prev) => [...prev, {
      id: newId,
      label: "",
      order: prev.length + 1,
      // Actif=True par défaut sur le premier champ de chaque nouvelle étape
      // (spec 2026-02-11 §7). Nouvelle étape = 1 champ requis actif par
      // défaut ; l'admin peut ajouter d'autres champs ou désactiver.
      required_fields: [{
        id: `new-rf-${Date.now()}`,
        label: "Champ 1",
        type: "text",
        options: [],
        active: true,
      }],
      // Legacy singleton conservé pour rétro-compat des lectures anciennes.
      required_field_active: true,
      required_field_type: "text",
      required_field_options: [],
      // Spec iter118a — liste multi-responsables (vide par défaut).
      responsible_user_ids: [],
      deadline_required: false,
      deadline_mode: "from_step",
      deadline_from_step_id: null,           // no anchor pre-selected
      deadline_days: 0,
      deadline_fixed: null,
    }]);
    setFreshStepIds((s) => new Set([...s, newId]));
    // Auto-clear the "Nouvelle" badge after FRESH_MS ms.
    setTimeout(() => {
      setFreshStepIds((s) => {
        if (!s.has(newId)) return s;
        const n = new Set(s); n.delete(newId); return n;
      });
    }, FRESH_MS);
  };
  const _stampInteracted = (idx) => {
    // Any interaction with a "fresh" step should immediately remove its
    // "Nouvelle" badge — spec expects it to disappear on interact.
    setSteps((prev) => {
      const st = prev[idx];
      if (st && freshStepIds.has(st.id)) {
        setFreshStepIds((s) => {
          const n = new Set(s); n.delete(st.id); return n;
        });
      }
      return prev;
    });
  };
  const updStep = (idx, patch) => {
    _stampInteracted(idx);
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const rmStep = (idx) =>
    setSteps((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })));

  // Spec iter212 (2026-02-28) — Suppression d'étape AVEC toast d'undo.
  // Sauve la step + son idx d'origine puis affiche un toast Sonner
  // 5 s avec bouton « Annuler ». Le toast s'auto-dismiss → laisse la
  // suppression effective ; le mail au créateur ne partira de toute
  // façon qu'à la sauvegarde du type (bouton « Enregistrer »), donc
  // l'undo est bien PRÉ-email.
  const rmStepWithUndo = (idx) => {
    setSteps((prev) => {
      if (idx < 0 || idx >= prev.length) return prev;
      const removed = prev[idx];
      const next = prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 }));
      // Toast affiché APRÈS le setSteps pour ne pas bloquer le rendu.
      setTimeout(() => {
        toast("Étape supprimée", {
          description: `« ${removed.label || "Étape"} » sera retirée à l'enregistrement.`,
          duration: 5000,
          action: {
            label: "Annuler",
            onClick: () => {
              // Ré-insère la step à son idx d'origine + rebase des orders.
              setSteps((cur) => {
                const rebuilt = [...cur];
                rebuilt.splice(Math.min(idx, rebuilt.length), 0, removed);
                return rebuilt.map((s, i) => ({ ...s, order: i + 1 }));
              });
              toast.success("Suppression annulée", {
                description: `« ${removed.label || "Étape"} » a été restaurée`,
              });
            },
          },
        });
      }, 0);
      return next;
    });
  };

  // ---- Drag & drop ---------------------------------------------------------
  const onDragStart = (i) => (e) => {
    dragFromRef.current = i;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(i)); } catch (_) { /* Firefox needs setData */ }
  };
  const onDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIdx !== i) setDragOverIdx(i);
  };
  const onDragLeave = () => setDragOverIdx(null);
  const onDrop = (targetIdx) => (e) => {
    e.preventDefault();
    const from = dragFromRef.current;
    dragFromRef.current = null;
    setDragOverIdx(null);
    if (from === null || from === targetIdx) return;
    setSteps((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(targetIdx, 0, moved);
      return next.map((s, i) => ({ ...s, order: i + 1 }));
    });
  };
  const onDragEnd = () => { dragFromRef.current = null; setDragOverIdx(null); };

  // ---- Submit --------------------------------------------------------------
  // Spec rev79 — validation nom court retirée : le short_name est
  // désormais toujours synchronisé avec le nom complet.
  // Spec rev106 (2026-02) — en mode "simple" (sans étape), on exige au
  // moins un champ personnalisé au-delà du pinned "Titre" — sinon le
  // document créé serait strictement vide.
  const _nonPinnedFields = fields.filter(
    (f) => (f.key || "").toLowerCase() !== PINNED_TITRE_KEY,
  );
  const _simpleModeOk =
    workflowMode !== "simple" || _nonPinnedFields.length > 0;
  const canSubmit = name.trim() &&
    _simpleModeOk &&
    fields.every((f) => f.label.trim()) &&
    steps.every((s) => s.label.trim() &&
      (!s.deadline_required ||
        s.deadline_mode === "fixed" ||
        (s.deadline_mode === "from_step" && s.deadline_from_step_id)));

  const submit = async () => {
    if (!canSubmit) return;
    if (autoTaskEnabled && autoTaskAssignees.length === 0) { toast.error("Tâche automatique : au moins un utilisateur assigné est requis"); return; }
    // Translate local "__idx__:N" placeholders back to real step ids.
    const stepIdByIdx = steps.map((s) =>
      s.id && !String(s.id).startsWith("new-") ? s.id : `pending-${Math.random().toString(36).slice(2)}`
    );
    const payload = {
      name: name.trim(),
      // Spec rev89 (2026-08-02) — BUG FIX : depuis rev79 le champ « Nom
      // court » a été retiré du formulaire, mais l'ancienne implémentation
      // envoyait encore ``short_name: name.trim()`` — ce qui déclenchait
      // ``Nom court trop long`` côté backend dès que le nom complet
      // dépassait 20 caractères (bloquait aussi la création de catégorie
      // en aval, puisque l'erreur remontait sur le PATCH type). On envoie
      // désormais une chaîne vide : le backend re-dérive automatiquement
      // via ``_derive_short_name(name)`` (tronqué proprement, jamais > 20).
      short_name: "",
      color: (color || "").trim() || null,
      // Spec rev75 (2026-08-02) — catégorie transversale optionnelle.
      // "" (menu "Aucune") → null en base pour signifier absence explicite.
      category_id: categoryId || null,
      location_required: lotLinked ? true : locationRequired,
      lot_linked: lotLinked,
      auto_task_enabled: autoTaskEnabled,
      auto_task_assignees: autoTaskEnabled ? autoTaskAssignees.map((a) => ({ user_id: a.user_id, role: a.role === "manager" ? "manager" : "executor" })) : [],
      workflow_mode: workflowMode,
      // Spec iter121 pt.4 (Feb 2026) — responsables niveau type.
      responsible_user_ids: (typeResponsibleIds || []).filter(Boolean),
      fields: (() => {
        // Ensure keys are unique per DocumentType — auto-suffix collisions
        // (e.g. two "Montant" fields → "montant", "montant_2"). Regardless
        // of what the user typed, the key is *always* derived from the
        // current label to avoid stale keys from earlier keystrokes.
        const used = new Set();
        return fields.map((f, i) => {
          let base = _slug(f.label);
          let key = base;
          let n = 2;
          while (used.has(key)) {
            key = `${base}_${n++}`;
          }
          used.add(key);
          return {
            id: f.id && !String(f.id).startsWith("new-") ? f.id : undefined,
            key,
            label: f.label.trim(),
            type: f.type || "text",
            required: !!f.required,
            options: f.type === "dropdown"
              ? (f.options || []).map((o) => String(o).trim()).filter(Boolean)
              : [],
            order: i + 1,
            full_width: !!f.full_width,
          };
        });
      })(),
      workflow_steps: (workflowMode === "dossier" ? [] : steps).map((s, i) => {
        let fromId = s.deadline_from_step_id;
        if (fromId && fromId !== CREATION_ANCHOR && String(fromId).startsWith("__idx__:")) {
          const j = parseInt(String(fromId).split(":")[1], 10);
          fromId = stepIdByIdx[j];
        }
        // Normalise the multi-required-fields list — strip transient
        // `new-*` ids so the backend re-generates real UUIDs, clean
        // dropdown options to trimmed non-empty strings only.
        const cleanedRF = (s.required_fields || []).map((f, k) => ({
          id: f.id && !String(f.id).startsWith("new-") ? f.id : undefined,
          label: (f.label || `Champ ${k + 1}`).trim() || `Champ ${k + 1}`,
          type: f.type || "text",
          options: f.type === "dropdown"
            ? (f.options || []).map((o) => String(o).trim()).filter(Boolean)
            : [],
          active: f.active !== false,
        }));
        // Legacy singleton mirrors required_fields[0] for backward compat.
        const first = cleanedRF[0] || { active: false, type: "text", options: [] };
        return {
          // Spec 2026-02-XX rev8 (Feb 2026 bugfix) — on envoie EXPLICITEMENT
          // le sentinel ``pending-XXX`` (au lieu de undefined) pour les
          // étapes fraîches. Le backend s'en sert comme clé de remapping
          // pour résoudre les ``deadline_from_step_id`` qui pointent vers
          // ces étapes fraîches ; sans cette clé partagée le lien
          // étape-fraîche → étape-fraîche est perdu à la première save.
          id: stepIdByIdx[i],
          label: s.label.trim(),
          order: i + 1,
          // Spec iter118a (Feb 2026) — LISTE de responsables (multi).
          // Migration ascendante : accepte un ancien state singleton
          // (``responsible_user_id``) et le convertit en liste 1-élément.
          responsible_user_ids: Array.isArray(s.responsible_user_ids)
            ? s.responsible_user_ids.filter(Boolean)
            : (s.responsible_user_id ? [s.responsible_user_id] : []),
          required_fields: cleanedRF,
          // DEPRECATED — kept in sync with required_fields[0].
          required_field_active: !!first.active,
          required_field_type: first.type || "text",
          required_field_options: first.options || [],
          deadline_required: !!s.deadline_required,
          deadline_mode: s.deadline_required ? (s.deadline_mode || "from_step") : null,
          deadline_from_step_id: s.deadline_required && s.deadline_mode === "from_step" ? fromId : null,
          deadline_days: s.deadline_required && s.deadline_mode === "from_step" ? Number(s.deadline_days) || 0 : 0,
          deadline_fixed: s.deadline_required && s.deadline_mode === "fixed" ? (s.deadline_fixed || null) : null,
        };
      }),
    };
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        // Spec rev134 (2026-02-06) — si la modification touche la
        // structure du workflow ET qu'au moins un document actif existe,
        // on ouvre la modale de confirmation avant d'envoyer la requête.
        // La modale demande à l'admin de choisir entre :
        //   • Propager     → PATCH avec ?propagate=true (comportement historique)
        //   • Futurs seul. → PATCH avec ?propagate=false (docs existants figés)
        // Spec rev137 (2026-02-06) — la modale de propagation doit se
        // déclencher pour TOUT changement structurel (étapes, délais ET
        // champs requis d'une étape existante). Le check précédent
        // Spec rev134 → rev145 (2026-02-06) — la modale de propagation
        // se déclenche UNIQUEMENT quand un vrai changement structurel
        // touche les documents existants :
        //   • ajout/suppression/réordre d'étape,
        //   • ajout/retrait de champ requis (ou toggle actif/optionnel),
        //   • changement de délai.
        // Elle NE se déclenche PAS pour un renommage, un changement de
        // couleur/catégorie/description (cosmétique).
        // Le backend renvoie `impacts_documents_content: bool` dans le
        // preview — on l'utilise ici directement.
        const activeDocs = impactPreview?.affected_docs_count || 0;
        const impactsContent = !!impactPreview?.impacts_documents_content;
        const willTriggerPropagation = isEdit && activeDocs > 0 && impactsContent;
        if (willTriggerPropagation) {
          // Spec iter118b — si le SEUL changement est un
          // ``responsibles_changed``, présenter la modale dédiée
          // (chips + accordéon docs + « Propager à N documents »).
          const allChanges = impactPreview?.changes || [];
          const respChanges = allChanges.filter((c) => c.kind === "responsibles_changed");
          const onlyResp = respChanges.length > 0 && respChanges.length === allChanges.length;
          setPendingSavePayload(payload);
          setSaving(false);
          if (onlyResp) {
            setRespPropagationChanges(respChanges);
            setRespPropagationDialogOpen(true);
          } else {
            setPropagationDialogOpen(true);
          }
          return;
        }
        // Spec iter164 (2026-02-25) — DEFENSIVE : quand la modale de
        // propagation n'est PAS déclenchée (changement cosmétique : nom,
        // couleur, catégorie, short_name, description…), on force
        // EXPLICITEMENT ``?propagate=false`` sur le PATCH. Historiquement
        // (pré-Iter161) le backend défaultait à ``true`` — un simple
        // renommage déclenchait alors une propagation SILENCIEUSE et LONGUE
        // à tous les documents actifs du type (mise à jour des labels et
        // snap_required_fields sur chaque étape). Le default est désormais
        // ``false`` côté backend, mais on garde ceinture ET bretelles au
        // niveau frontend pour éviter toute régression future.
        const resp = await api.patch(
          `/document-types/${editing.id}?propagate=false`,
          payload,
        );
        saved = resp.data;
        // Spec iter164 (2026-02-25) — Toast dédié pour un renommage
        // « propre » : coche verte + libellé explicite « 0 document
        // existant modifié » pour rassurer visuellement l'admin qu'AUCUN
        // document n'a été touché. Détection = le nom envoyé diffère
        // du nom d'origine. Les autres changements cosmétiques
        // (couleur, catégorie…) gardent le toast générique.
        const originalName = (editing?.name || "").trim();
        const newName = (payload.name || "").trim();
        const isRename = originalName && newName && originalName !== newName;
        if (isRename) {
          toast.success("Renommage appliqué", {
            description: "0 document existant modifié",
          });
        } else {
          toast.success("Type mis à jour", {
            description: "Modification enregistrée",
          });
        }
      } else {
        const resp = await api.post("/document-types", payload);
        saved = resp.data;
        toast.success("Type créé");
      }
      onOpenChange(false);
      onSaved?.(saved);
    } catch (err) {
      toast.error("Impossible d'enregistrer", { description: err.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  // Spec rev134 (2026-02-06) — envoie effectif du payload en attente avec
  // le drapeau `propagate` choisi dans la modale.
  const finalizeSaveWithPropagation = async (propagate) => {
    if (!pendingSavePayload || !editing?.id) return;
    setSaving(true);
    try {
      const resp = await api.patch(
        `/document-types/${editing.id}?propagate=${propagate ? "true" : "false"}`,
        pendingSavePayload,
      );
      toast.success("Type mis à jour", {
        description: propagate
          ? `Modification propagée aux documents existants.`
          : `Modification appliquée aux futurs documents uniquement.`,
      });
      setPropagationDialogOpen(false);
      setRespPropagationDialogOpen(false);
      setRespPropagationChanges([]);
      setPendingSavePayload(null);
      onOpenChange(false);
      onSaved?.(resp.data);
    } catch (err) {
      toast.error("Impossible d'enregistrer", { description: err.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-card border-[#3A3A3A] max-w-4xl max-h-[92vh] overflow-y-auto"
        data-testid="doctype-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifier le type" : "Nouveau type de document"}</DialogTitle>
        </DialogHeader>

        {/* Spec rev117 (2026-02) — Onglets Configuration / Historique.
            L'onglet Historique n'est proposé qu'en mode ÉDITION (aucun
            journal à la création). */}
        {isEdit && (
          <OutlookTabs module="documents" value={activeTab} onChange={setActiveTab} testId="doctype-tabs" style={{ padding: 4, marginBottom: 12, background: "#101013", border: "1px solid #26262b", borderRadius: 12, width: "fit-content" }}
            tabs={[{ key: "config", label: "Configuration", icon: "config", testId: "doctype-tab-config" }, { key: "history", label: "Historique", icon: "history", testId: "doctype-tab-history" }]} />
        )}

        {/* --- Historique tab -------------------------------------------- */}
        {isEdit && activeTab === "history" && (
          <div data-testid="doctype-history-tab"><HistoryPanel entityType="document_type" entityId={editing.id} label={editing.label || editing.name} entityLabel="le type de document" height={480} testId="doctype-history" /></div>
        )}

        {/* --- Configuration tab ----------------------------------------- */}
        {(!isEdit || activeTab === "config") && (
        <>
        {/* Spec rev106 (2026-02) — Choix Avec/Sans étape : 2 cartes cliquables
            en tête de formulaire, présentées UNIQUEMENT à la création. En
            édition on gèle le mode (pas de conversion a posteriori) : on
            affiche une petite mention informative à la place. */}
        {!isEdit ? (
          <div className="grid gap-2 mb-4" style={{ gridTemplateColumns: "1fr 1fr 1fr" }} data-testid="doctype-mode-cards">
            {MODE_CARDS.map((c) => {
              const active = modeGroup(workflowMode) === c.key;
              const st = modeCardStyle(c.key, active);
              const Icon = c.Icon;
              return (
                <button key={c.key} type="button" onClick={() => setWorkflowMode(c.key)} className="relative flex items-start gap-3 p-4 rounded-lg text-left transition-all"
                  style={{ background: st.background, border: st.border, boxShadow: st.boxShadow }}
                  data-testid={`doctype-mode-card-${c.key === "simple" ? "simple" : c.key === "dossier" ? "dossier" : "stepped"}`} aria-pressed={active}>
                  <span className="flex items-center justify-center rounded-lg shrink-0 mt-0.5" style={{ width: 34, height: 34, borderRadius: 7, ...st.icon }}><Icon size={c.key === "dossier" ? 15 : 17} strokeWidth={2.2} /></span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold" style={{ color: st.label }}>{c.label}</div>
                    <div className="text-[11px] mt-0.5" style={{ color: st.hint }}>{c.hint}</div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : modeUnlocked ? (
          // Spec rev147 — mode déverrouillé : on affiche les 2 cartes
          // exactement comme à la création, avec en plus un lien
          // « Annuler le changement » qui revient au mode d'origine et
          // reverrouille.
          <div className="mb-4">
            <div className="grid gap-2" style={{ gridTemplateColumns: "1fr 1fr 1fr" }} data-testid="doctype-mode-cards-edit">
              {MODE_CARDS.map((c) => {
                const active = modeGroup(workflowMode) === c.key;
                const st = modeCardStyle(c.key, active);
                const Icon = c.Icon;
                const targetKey = c.key === "simple" ? "simple" : c.key === "dossier" ? "dossier" : "stepped";
                return (
                  <button key={c.key} type="button"
                    onClick={() => { if (active) return; setModeChangeConfirmTarget(targetKey); setModeChangeConfirmOpen(true); }}
                    className="relative flex items-start gap-3 p-4 rounded-lg text-left transition-all"
                    style={{ background: st.background, border: st.border, boxShadow: st.boxShadow }}
                    data-testid={`doctype-mode-card-edit-${targetKey}`} aria-pressed={active}>
                      <span className="flex items-center justify-center rounded-lg shrink-0 mt-0.5" style={{ width: 34, height: 34, borderRadius: 7, ...st.icon }}><Icon size={c.key === "dossier" ? 15 : 17} strokeWidth={2.2} /></span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold" style={{ color: st.label }}>{c.label}</div>
                      <div className="text-[11px] mt-0.5" style={{ color: st.hint }}>{c.hint}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            {workflowMode !== originalWorkflowMode && (
              <button
                type="button"
                onClick={() => {
                  setWorkflowMode(originalWorkflowMode);
                  setModeUnlocked(false);
                }}
                className="mt-2 text-[11px] text-muted-foreground hover:text-white transition-colors underline underline-offset-2"
                data-testid="doctype-mode-cancel-change"
              >
                Annuler le changement de mode
              </button>
            )}
          </div>
        ) : (
          <div
            className="mb-4 flex items-center gap-2 text-[11px] px-3 py-2 rounded"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", color: "#B0B3B8" }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: workflowMode === "simple" ? "#8B5CF6" : workflowMode === "dossier" ? "#D99B1E" : "#0078D4" }}
            />
            Mode : <strong className="text-white">{workflowMode === "simple" ? "Sans étape" : workflowMode === "dossier" ? "Dossier" : "Avec étapes"}</strong>
            <button
              type="button"
              onClick={() => setModeUnlocked(true)}
              className="ml-auto text-[11px] font-semibold px-2.5 py-1 rounded transition-colors"
              style={{
                background: "rgba(0,120,212,0.14)",
                border: "1px solid rgba(0,120,212,0.35)",
                color: "#5AB0F0",
              }}
              data-testid="doctype-mode-change-btn"
            >
              Changer le mode
            </button>
          </div>
        )}

        <div>
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground block mb-1">
            Nom du type <span className="text-red-500">*</span>
          </label>
          <Input
            value={name}
            onChange={(e) => {
              const v = e.target.value;
              setName(v);
              // Spec rev79 — short_name toujours = name (champ retiré).
              setShortName(v);
            }}
            placeholder="Ex : Chèque reçu"
            data-testid="doctype-name"
            autoFocus
          />

          {/* Spec rev79 (2026-08-02) — Le champ « Nom court (badge) » est
              retiré du formulaire : les badges dans le tableau Documents
              et ailleurs affichent désormais directement le nom complet
              (les cellules gèrent l'ellipsis physique en display block +
              w-full). Le champ ``short_name`` reste en base et dans le
              payload, mais synchronisé automatiquement avec le nom
              complet — plus jamais divergent. */}
          {/* Spec 2026-02-XX rev15 (point H) — génération de couleur
              100% automatique à la CRÉATION. Le color-picker n'apparaît
              qu'en mode ÉDITION (rev10). Cela évite toute interaction
              inutile à l'admin quand il crée un type : la couleur est
              choisie côté serveur avec 2 contraintes :
                1. Distincte des couleurs des autres types actifs.
                2. Lisible sur fond sombre #1B1B1B (contraste WCAG AA).
              Cf. `_autoassign_type_color` dans document_type_routes.py. */}
          {isEdit && (
            <div className="flex items-center gap-3 mt-3">
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground flex-1">
                Couleur du badge
                <div className="text-[10px] normal-case tracking-normal text-muted-foreground/70 mt-0.5">
                  Vous pouvez ajuster la couleur assignée automatiquement.
                </div>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={color || "#EF4444"}
                  onChange={(e) => setColor(e.target.value.toUpperCase())}
                  className="w-10 h-8 rounded cursor-pointer border border-white/10 bg-transparent"
                  data-testid="doctype-color-input"
                  aria-label="Couleur du badge"
                />
                {color && (
                  <>
                    <span className="text-[11px] font-mono text-muted-foreground">{color}</span>
                    <button
                      type="button"
                      onClick={() => setColor("")}
                      className="text-[10px] underline text-muted-foreground hover:text-white"
                      data-testid="doctype-color-reset"
                      title="Réinitialiser (auto-assign côté serveur)"
                    >
                      Auto
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
          {!isEdit && (
            <div className="mt-3" data-testid="doctype-color-preview-block">
              {/* Spec 2026-02-XX rev16 (point I) — preview live de la
                  couleur qui sera assignée automatiquement. Feedback
                  visuel uniquement, aucune interaction requise. La
                  couleur est fetchée via /api/document-types/next-color
                  qui utilise le même algo WCAG-safe que la création. */}
              <NextColorPreview name={name} shortName={name} />
            </div>
          )}
          {/* Spec rev75 (2026-08-02) — champ Catégorie (optionnel).
              Menu déroulant + création inline avec preview de couleur
              auto-générée (même algo WCAG que les types, mais palette
              indépendante). */}
          <div className="mt-3" data-testid="doctype-category-block">
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
              Catégorie
              <span className="ml-2 text-[10px] normal-case tracking-normal text-muted-foreground/70">Optionnel</span>
            </label>
            <div className="mt-1.5 flex items-center gap-2">
              <DSelect
                value={categoryId || ""}
                onChange={(e) => {
                  setCreatingCategory(false);
                  setCategoryId(e.target.value);
                }}
                className="flex-1 h-9 rounded-md px-2 text-sm focus:outline-none focus:ring-1 focus:ring-vertec-blue"
                style={{ background: "#1B1B1B", border: "1px solid #3A3A3A", color: "#EDEDED" }}
                data-testid="doctype-category-select"
              >
                <option value="">— Aucune catégorie —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </DSelect>
              {/* Spec rev81 (2026-08-02) — bouton pilule translucide bleu pour
                  extraire l'action « Créer une catégorie » du menu déroulant. */}
              <button
                type="button"
                onClick={() => { setCreatingCategory(true); setCategoryId(""); }}
                disabled={creatingCategory}
                className="inline-flex items-center gap-1 text-[11px] font-medium px-3 h-9 rounded-full whitespace-nowrap transition-colors disabled:opacity-40"
                style={{
                  background: "rgba(90,158,224,0.14)",
                  color: "#5A9EE0",
                  border: "1px solid rgba(90,158,224,0.28)",
                }}
                title="Créer une nouvelle catégorie"
                data-testid="doctype-category-new-btn"
              >
                <span className="text-[13px] leading-none">+</span>
                <span>Créer une catégorie</span>
              </button>
              {!creatingCategory && categoryId && (() => {
                const cat = categories.find((c) => c.id === categoryId);
                if (!cat) return null;
                return (
                  <span
                    className="inline-flex items-center text-[11px] font-semibold uppercase px-3 py-1 rounded-full whitespace-nowrap"
                    style={{
                      background: `${cat.color}1F`,
                      border: `1px solid ${cat.color}55`,
                      color: cat.color,
                    }}
                    data-testid="doctype-category-badge-preview"
                    title={cat.name}
                  >
                    {cat.name}
                  </span>
                );
              })()}
            </div>
            {creatingCategory && (
              <div className="mt-2 rounded-md p-3" style={{ background: "#161616", border: "1px solid #2A2A2A" }} data-testid="doctype-category-creator">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                  Nouvelle catégorie
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Nom de la catégorie (ex. Achat, Vente)"
                    maxLength={40}
                    className="flex-1 h-9 rounded-md px-2 text-sm focus:outline-none focus:ring-1 focus:ring-vertec-blue"
                    style={{ background: "#1B1B1B", border: "1px solid #3A3A3A", color: "#EDEDED" }}
                    data-testid="doctype-category-new-name"
                  />
                  {newCategoryName.trim() && newCategoryColor && (
                    <span
                      className="inline-flex items-center text-[11px] font-semibold uppercase px-3 py-1 rounded-full whitespace-nowrap"
                      style={{
                        background: `${newCategoryColor}1F`,
                        border: `1px solid ${newCategoryColor}55`,
                        color: newCategoryColor,
                      }}
                      data-testid="doctype-category-new-preview"
                      title={`Aperçu · couleur auto ${newCategoryColor}`}
                    >
                      {newCategoryName.trim()}
                    </span>
                  )}
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-[10px] text-muted-foreground italic">
                    Couleur auto-assignée (contraste WCAG safe · distincte des autres catégories).
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { setCreatingCategory(false); setNewCategoryName(""); }}
                      className="text-[11px] text-muted-foreground hover:text-white"
                      data-testid="doctype-category-new-cancel"
                    >Annuler</button>
                    <button
                      type="button"
                      disabled={!newCategoryName.trim() || savingCategory}
                      onClick={async () => {
                        setSavingCategory(true);
                        try {
                          const { data } = await api.post("/document-type-categories", { name: newCategoryName.trim() });
                          setCategories((prev) => [...prev, data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr")));
                          setCategoryId(data.id);
                          setCreatingCategory(false);
                          setNewCategoryName("");
                          toast.success("Catégorie créée");
                        } catch (err) {
                          toast.error("Impossible de créer", { description: err.response?.data?.detail || "" });
                        } finally {
                          setSavingCategory(false);
                        }
                      }}
                      className="text-[11px] font-medium px-3 py-1 rounded-md disabled:opacity-40"
                      style={{ background: "#2B579A", color: "#FFFFFF" }}
                      data-testid="doctype-category-new-save"
                    >
                      {savingCategory ? "Création…" : "Créer la catégorie"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* iter359 pt.6/7 — interrupteurs Siège / Projet (Obligatoire / Facultatif) et « Lié à un lot » */}
          <div className="mt-3 grid gap-2.5" data-testid="doctype-location-block">
            <div className="flex items-center gap-2.5">
              <ToggleSwitch checked={lotLinked ? true : locationRequired} onChange={setLocationRequired} disabled={lotLinked} testid="doctype-location-required"
                label={<span className="flex items-center gap-1.5"><MapPin size={11} /> Siège / Projet · <span style={{ color: (lotLinked || locationRequired) ? "#4fc79c" : "#8a8a93" }} data-testid="doctype-location-required-label">{(lotLinked || locationRequired) ? "Obligatoire" : "Facultatif"}</span>{lotLinked && <Lock size={11} style={{ color: "#8a8a93" }} data-testid="doctype-location-lock" />}</span>}
                hint={lotLinked ? "Verrouillé : un type lié à un lot exige toujours un projet." : "Facultatif pour les documents purement internes."} />
            </div>
            <div className="flex items-center gap-2.5">
              <ToggleSwitch checked={lotLinked} onChange={setLotLinked} testid="doctype-lot-linked"
                label={<span className="flex items-center gap-1.5"><LinkIcon size={11} /> Lié à un lot · <span style={{ color: lotLinked ? "#4fc79c" : "#8a8a93" }} data-testid="doctype-lot-linked-label">{lotLinked ? "Oui" : "Non"}</span></span>}
                hint="Oui : à la création, le lot du projet est obligatoire et le document apparaît dans « Workflow documents » du Dashboard du lot." />
            </div>
          </div>

          {/* Spec iter121 pt.4 (Feb 2026) — Responsables au niveau type.
              Verrou Premier Admin : si le Premier Admin figure dans la
              liste, seul lui peut la modifier. */}
          <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-[160px_1fr] items-start gap-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground pt-1.5">
              Responsables du type
            </div>
            <div className="min-w-0">
              <StepResponsibleSelect
                values={typeResponsibleIds || []}
                onChange={setTypeResponsibleIds}
                testId="doctype-type-responsibles"
              />
              <div className="text-[10.5px] text-muted-foreground mt-1.5 italic">
                Si le Premier Admin figure dans cette liste, lui seul peut la modifier.
              </div>
            </div>
          </div>

          {/* Spec 2026-07-24 feature #7 — mode de validation du workflow.
              Spec rev106 (2026-02) — masqué en mode "simple" (sans étape). */}
          {workflowMode !== "simple" && workflowMode !== "dossier" && (
          <div className="mt-3 flex items-start gap-4 flex-wrap" data-testid="doctype-workflow-mode-group">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0 pt-1.5">
              Ordre des étapes
            </div>
            <label className="flex items-center gap-2 text-[12px] cursor-pointer select-none">
              <input
                type="radio" name="workflow-mode" value="sequential"
                checked={workflowMode === "sequential"}
                onChange={() => setWorkflowMode("sequential")}
                className="h-4 w-4 accent-[#2B579A]"
                data-testid="doctype-mode-sequential"
              />
              <span>Séquentiel <span className="text-muted-foreground text-[11px]">— dans l&apos;ordre imposé</span></span>
            </label>
            <label className="flex items-center gap-2 text-[12px] cursor-pointer select-none">
              <input
                type="radio" name="workflow-mode" value="parallel"
                checked={workflowMode === "parallel"}
                onChange={() => setWorkflowMode("parallel")}
                className="h-4 w-4 accent-[#2B579A]"
                data-testid="doctype-mode-parallel"
              />
              <span>Non-séquentiel <span className="text-muted-foreground text-[11px]">— n&apos;importe quel ordre</span></span>
            </label>
          </div>
          )}
        </div>

        {/* ==== Champs du document ========================================= */}
        <div className="mt-3 pt-3 border-t border-white/5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
              Champs du document ({fields.length})
            </div>
            <Button
              size="sm"
              onClick={addField}
              className="h-8 text-xs border-0 text-white gap-1 px-3"
              style={{
                background: "linear-gradient(135deg, #2B579A 0%, #1E3E70 100%)",
                boxShadow: "0 2px 6px rgba(43,87,154,0.35)",
              }}
              data-testid="doctype-add-field"
            >
              <Plus size={13} /> Ajouter un champ
            </Button>
          </div>

          {fields.length === 0 ? (
            <div
              className="text-sm text-muted-foreground italic py-6 text-center rounded-lg"
              style={{ border: "2px dashed rgba(255,255,255,0.06)" }}
            >
              Aucun champ. Ajoutez les données à saisir sur le document (ex : Nom du client, Date, Banque…).
            </div>
          ) : (
            <div className="space-y-1.5">
              {fields.map((f, i) => (
                <FieldRow
                  key={f.id || i}
                  index={i}
                  field={f}
                  typeId={isEdit ? editing?.id : null}
                  locked={(f.key || "").toLowerCase() === PINNED_TITRE_KEY}
                  onUpdate={(patch) => updField(i, patch)}
                  onRemove={() => rmField(i)}
                  onDragStart={onFieldDragStart(i)}
                  onDragOver={onFieldDragOver(i)}
                  onDragLeave={() => setDragFieldOverIdx(null)}
                  onDrop={onFieldDrop(i)}
                  onDragEnd={onFieldDragEnd}
                  isDragOver={dragFieldOverIdx === i}
                />
              ))}
              <button
                type="button"
                onClick={addField}
                data-testid="doctype-add-field-bottom"
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md text-xs transition-colors mt-1"
                style={{
                  border: "1.5px dashed rgba(43,87,154,0.45)",
                  color: "#5A9EE0",
                  background: "rgba(43,87,154,0.04)",
                }}
              >
                <Plus size={12} /> Ajouter un champ
              </button>
            </div>
          )}
        </div>

        {/* ==== Workflow steps — unified section ============================
            Spec rev106 (2026-02) — la section entière disparaît en mode
            "simple". Elle est incompatible avec des documents sans étape. */}
        {workflowMode !== "simple" && workflowMode !== "dossier" && (
        <div className="mt-3 pt-3 border-t border-white/5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
              {workflowMode === "dossier" ? "Étapes du dossier" : "Étapes du workflow"} ({steps.length})
            </div>
            <Button
              size="sm"
              onClick={addStep}
              className="h-8 text-xs border-0 text-white gap-1 px-3"
              style={{
                background: "linear-gradient(135deg, #2B579A 0%, #1E3E70 100%)",
                boxShadow: "0 2px 6px rgba(43,87,154,0.35)",
              }}
              data-testid="doctype-add-step"
            >
              <Plus size={13} /> Ajouter une étape
            </Button>
          </div>

          {steps.length === 0 ? (
            <div
              className="text-sm text-muted-foreground italic py-8 text-center rounded-lg"
              style={{ border: "2px dashed rgba(255,255,255,0.08)" }}
            >
              {workflowMode === "dossier" ? "Aucune étape définie. L'utilisateur pourra en ajouter à la création du document." : "Aucune étape. Cliquez sur « Ajouter une étape » pour commencer."}
            </div>
          ) : (
            <div className="space-y-3">
              {steps.map((s, i) => (
                <StepCard
                  key={s.id || i}
                  index={i}
                  step={s}
                  earlierSteps={steps.slice(0, i)}
                  onUpdate={(patch) => updStep(i, patch)}
                  onRemove={() => setStepToDelete({ idx: i, label: s.label || `Étape ${i + 1}` })}
                  onDragStart={onDragStart(i)}
                  onDragOver={onDragOver(i)}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop(i)}
                  onDragEnd={onDragEnd}
                  isDragOver={dragOverIdx === i}
                  justAdded={freshStepIds.has(s.id)}
                />
              ))}
              <button
                type="button"
                onClick={addStep}
                data-testid="doctype-add-step-bottom"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm transition-colors mt-1"
                style={{
                  border: "2px dashed rgba(43,87,154,0.55)",
                  color: "#5A9EE0",
                  background: "rgba(43,87,154,0.04)",
                }}
              >
                <Plus size={14} />
                <span className="font-medium">Ajouter une étape</span>
              </button>
            </div>
          )}
        </div>
        )}
        <AutoTaskSection enabled={autoTaskEnabled} onEnabledChange={setAutoTaskEnabled} assignees={autoTaskAssignees} onAssigneesChange={setAutoTaskAssignees} />
        </>
        )}

        {/* Spec rev147 (2026-02-06) — Bannière d'impact DÉTAILLÉE :
            liste des changements réels détectés par le backend
            (add/remove/change de champ, add/remove/reorder d'étape,
            deadline, etc.). Affichée en édition, quand au moins un
            document actif utilise ce type ET qu'un vrai changement
            structurel est détecté. */}
        {isEdit
          && activeTab === "config"
          && (impactPreview?.affected_docs_count || 0) > 0
          && !!impactPreview?.impacts_documents_content
          && (impactPreview?.changes || []).length > 0 && (
          <div
            className="mt-4 rounded-md overflow-hidden"
            style={{
              background: "rgba(249,115,22,0.06)",
              border: "1px solid rgba(249,115,22,0.35)",
            }}
            data-testid="doctype-impact-banner"
          >
            <div
              className="flex items-center gap-2 px-3 py-2 font-semibold text-[12.5px]"
              style={{
                background: "rgba(249,115,22,0.10)",
                borderBottom: "1px solid rgba(249,115,22,0.25)",
                color: "#FBA574",
              }}
            >
              <AlertTriangle size={13} strokeWidth={2.4} />
              Cette modification affecte {impactPreview.affected_docs_count} document{impactPreview.affected_docs_count > 1 ? "s" : ""} existant{impactPreview.affected_docs_count > 1 ? "s" : ""}
            </div>
            <div
              className="p-2 space-y-1"
              style={{
                background: "rgba(0,0,0,0.35)",
              }}
              data-testid="doctype-impact-banner-changes"
            >
              {(impactPreview.changes || []).map((c, i) => {
                // Mappe chaque `kind` sur un symbole + couleur.
                //   +  vert  → ajout (added, field_added)
                //   −  rouge → suppression (removed, field_removed)
                //   ~  jaune → modification (label_changed, deadline_changed,
                //              required_fields_changed, field_changed,
                //              reordered)
                const isAdd = c.kind === "added" || c.kind === "field_added";
                const isRemove = c.kind === "removed" || c.kind === "field_removed";
                const isChange = !isAdd && !isRemove;
                const symbol = isAdd ? "+" : isRemove ? "−" : "~";
                const symbolColor = isAdd ? "#22C55E" : isRemove ? "#EF4444" : "#EAB308";
                const symbolBg = isAdd ? "rgba(34,197,94,0.16)"
                  : isRemove ? "rgba(239,68,68,0.16)"
                    : "rgba(234,179,8,0.16)";
                // Libellé humain court par kind.
                const KIND_LABELS = {
                  added: "Étape ajoutée",
                  removed: "Étape supprimée",
                  reordered: "Étape réordonnée",
                  label_changed: "Étape renommée",
                  deadline_changed: "Délai modifié",
                  required_fields_changed: "Champs requis modifiés",
                  responsibles_changed: "Responsables modifiés",
                  field_added: "Champ ajouté",
                  field_removed: "Champ supprimé",
                  field_changed: "Champ modifié",
                };
                const kindLabel = KIND_LABELS[c.kind] || c.kind;
                return (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-2 py-1 rounded"
                    style={{ background: "rgba(255,255,255,0.02)" }}
                    data-testid={`doctype-impact-banner-item-${i}`}
                  >
                    <span
                      className="inline-flex items-center justify-center rounded-full font-bold shrink-0"
                      style={{
                        width: 18, height: 18,
                        background: symbolBg, color: symbolColor,
                        fontSize: 12, lineHeight: 1,
                      }}
                    >{symbol}</span>
                    <span className="text-[11.5px] font-semibold whitespace-nowrap" style={{ color: symbolColor }}>
                      {kindLabel} :
                    </span>
                    <span className="text-[11.5px] truncate" style={{ color: "#EDEDED" }}>
                      « {c.label || "(sans nom)"} »
                    </span>
                    {c.detail && (
                      <>
                        <span aria-hidden="true" style={{ color: "rgba(255,255,255,0.20)" }}>—</span>
                        <span className="text-[11px] italic truncate" style={{ color: "#9CA3AF" }}>
                          {c.detail}
                        </span>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Spec rev137 (2026-02-06) — Bannière automatique supprimée :
            la modale de choix « Propager / Futurs seulement » (rev134)
            remplace intégralement l'ancien bandeau d'info. */}

        {(!isEdit || activeTab === "config") && (
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={!canSubmit || saving}
            className="text-white border-0 disabled:opacity-50"
            style={{ background: "#2B579A" }}
            data-testid="doctype-save"
          >
            {saving ? "Enregistrement…" : (isEdit ? "Enregistrer" : "Créer le type")}
          </Button>
        </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
    {/* --- Modale de confirmation de propagation (spec rev134) ------- */}
    <PropagationChoiceDialog
      open={propagationDialogOpen}
      onOpenChange={(v) => { setPropagationDialogOpen(v); if (!v) setPendingSavePayload(null); }}
      affectedCount={impactPreview?.affected_docs_count || 0}
      saving={saving}
      onChoose={finalizeSaveWithPropagation}
    />
    {/* --- Modale dédiée propagation multi-responsables (iter118b) --- */}
    <ResponsablesPropagationDialog
      open={respPropagationDialogOpen}
      onOpenChange={(v) => {
        setRespPropagationDialogOpen(v);
        if (!v) { setPendingSavePayload(null); setRespPropagationChanges([]); }
      }}
      typeId={editing?.id}
      typeName={editing?.name}
      affectedCount={impactPreview?.affected_docs_count || 0}
      changes={respPropagationChanges}
      saving={saving}
      onConfirm={() => finalizeSaveWithPropagation(true)}
    />
    {/* --- Modale de confirmation du changement de mode (spec rev147) --- */}
    <ModeChangeConfirmDialog
      open={modeChangeConfirmOpen}
      onOpenChange={setModeChangeConfirmOpen}
      target={modeChangeConfirmTarget}
      affectedCount={impactPreview?.affected_docs_count || 0}
      onConfirm={() => {
        // Applique le mode cible et referme la modale. La modale de
        // propagation rev134 se chargera de proposer « Propager /
        // Futurs seulement » au moment de la sauvegarde (pour la
        // transition simple→stepped uniquement).
        if (modeChangeConfirmTarget === "simple") {
          setWorkflowMode("simple");
        } else if (modeChangeConfirmTarget === "dossier") {
          setWorkflowMode("dossier");
        } else {
          setWorkflowMode("sequential");
        }
        setModeChangeConfirmOpen(false);
        setModeChangeConfirmTarget(null);
      }}
    />
    {/* --- Modale de confirmation suppression d'étape (spec iter211) --- */}
    <ConfirmDeleteStepDialog
      open={!!stepToDelete}
      onOpenChange={(v) => { if (!v) setStepToDelete(null); }}
      stepLabel={stepToDelete?.label}
      stepIndex={stepToDelete != null ? stepToDelete.idx + 1 : null}
      willNotifyCreator={isEdit}
      onConfirm={() => {
        if (stepToDelete == null) return;
        rmStepWithUndo(stepToDelete.idx);
        setStepToDelete(null);
      }}
    />
    </>
  );
}

export const _reserved = null;


// ---------------------------------------------------------------------------
// HistoryEntryCard (spec rev117) — rend une entrée du journal des
// propagations avec date, actor, description compacte des changements
// et un bouton "Voir les N docs" si affected_docs_count > 0.
// ---------------------------------------------------------------------------
function HistoryEntryCard({ entry, onSeeDocs, typeId, onRetroApplied }) {
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const dt = new Date(entry.created_at);
  const dateStr = isNaN(dt.getTime())
    ? "—"
    : dt.toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });

  // Spec iter209sextans (2026-02-28) — Rendu dédié « Étape supprimée
  // récemment » (event_type=step_deleted). Journalisé côté backend au
  // moment de la sauvegarde d'un type de doc quand un admin retire une
  // étape. Distinct des logs de propagation classiques.
  if (entry.event_type === "step_deleted") {
    return (
      <div
        className="rounded-md p-3 flex items-center gap-3"
        style={{
          background: "rgba(239,68,68,0.08)",
          border: "1px solid rgba(239,68,68,0.35)",
        }}
        data-testid={`doctype-history-step-deleted-${entry.id}`}
      >
        <div
          className="flex items-center justify-center rounded-full shrink-0"
          style={{
            width: 32, height: 32,
            background: "rgba(239,68,68,0.18)",
            color: "#EF4444",
          }}
        >
          <Trash2 size={14} strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-semibold" style={{ color: "#F87171" }}>
            Étape supprimée · {entry.step_label || "—"}
          </div>
          <div className="text-[11.5px] mt-0.5" style={{ color: "#D1D5DB" }}>
            <span className="opacity-70">par</span>{" "}
            <span className="font-medium">{entry.actor_name || "—"}</span>{" "}
            <span className="opacity-50">·</span>{" "}
            <span className="tabular-nums opacity-80">{dateStr}</span>
          </div>
        </div>
      </div>
    );
  }

  const changes = entry.changes || [];
  const KIND_META = {
    added:                    { label: "Étape ajoutée",          color: "#22C55E", icon: Plus },
    removed:                  { label: "Étape supprimée",         color: "#EF4444", icon: Trash2 },
    reordered:                { label: "Étape réordonnée",        color: "#5A9EE0", icon: ArrowRightLeft },
    label_changed:            { label: "Nom d'étape modifié",     color: "#EAB308", icon: FileText },
    deadline_changed:         { label: "Délai modifié",           color: "#F97316", icon: Calendar },
    required_fields_changed:  { label: "Champs requis modifiés",  color: "#8B5CF6", icon: ListChecks },
  };
  return (
    <div
      className="rounded-md p-3"
      style={{
        background: "#181818",
        border: "1px solid #2A2A2A",
      }}
      data-testid={`doctype-history-entry-${entry.id}`}
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 text-[11px]" style={{ color: "#B0B3B8" }}>
          <span className="tabular-nums font-medium" style={{ color: "#E5E5E5" }}>{dateStr}</span>
          <span aria-hidden="true" style={{ color: "rgba(255,255,255,0.2)" }}>·</span>
          {/* Spec iter82 (Feb 2026) — nom auteur remplacé par avatar
              cliquable (popover). */}
          <span className="inline-flex items-center gap-1.5">
            par
            <UserAvatar
              user={{
                id: entry.created_by || entry.created_by_name,
                first_name: (entry.created_by_name || "").split(" ")[0] || "",
                last_name: (entry.created_by_name || "").split(" ").slice(1).join(" ") || "",
              }}
              size={18}
              subtitle="Auteur de la modification"
            />
          </span>
        </div>
        {entry.affected_docs_count > 0 && (
          <button
            type="button"
            onClick={onSeeDocs}
            className="text-[11px] underline underline-offset-2 hover:brightness-125 whitespace-nowrap"
            style={{ color: "#5A9EE0" }}
            data-testid={`doctype-history-entry-see-docs-${entry.id}`}
          >
            {entry.affected_docs_count} document{entry.affected_docs_count > 1 ? "s" : ""} impacté{entry.affected_docs_count > 1 ? "s" : ""} →
          </button>
        )}
        {entry.affected_docs_count === 0 && (
          <span className="text-[11px] italic" style={{ color: "#6B7280" }}>Aucun doc actif</span>
        )}
      </div>
      <div className="flex flex-col gap-1">
        {changes.map((c, i) => {
          const meta = KIND_META[c.kind] || { label: c.kind, color: "#9CA3AF", icon: FileText };
          const Icon = meta.icon;
          let suffix = null;
          if (c.kind === "reordered" && c.from && c.to) {
            suffix = <span className="text-[11px] tabular-nums" style={{ color: "#8B8F94" }}>position {c.from} → {c.to}</span>;
          } else if (c.kind === "added" && c.to) {
            suffix = <span className="text-[11px] tabular-nums" style={{ color: "#8B8F94" }}>position {c.to}</span>;
          } else if (c.kind === "removed" && c.from) {
            suffix = <span className="text-[11px] tabular-nums" style={{ color: "#8B8F94" }}>ancienne position {c.from}</span>;
          } else if (c.detail) {
            suffix = <span className="text-[11px]" style={{ color: "#8B8F94" }}>{c.detail}</span>;
          }
          return (
            <div key={i} className="flex items-center gap-2 text-[12px]">
              <span
                className="inline-flex items-center justify-center rounded-full shrink-0"
                style={{
                  width: 20, height: 20,
                  background: `${meta.color}22`,
                  color: meta.color,
                }}
              >
                <Icon size={11} strokeWidth={2.4} />
              </span>
              <span className="font-medium whitespace-nowrap" style={{ color: meta.color }}>{meta.label}</span>
              <span aria-hidden="true" style={{ color: "rgba(255,255,255,0.15)" }}>·</span>
              <span className="truncate" style={{ color: "#E5E5E5" }}>« {c.label || "Étape sans nom"} »</span>
              {suffix && <>
                <span aria-hidden="true" style={{ color: "rgba(255,255,255,0.15)" }}>·</span>
                {suffix}
              </>}
            </div>
          );
        })}
      </div>
      {/* Spec rev135 (2026-02-06) — Bandeau « cascade rétro » quand la
          propagation a été refusée. Bouton bleu pour rattraper la
          propagation sans repasser par le formulaire. Après clic
          réussi, on affiche un état "Appliquée" persistant. */}
      {(() => {
        const rs = entry.retro_summary || {};
        const skipped = !!rs.propagation_skipped && !applied;
        const alreadyApplied = !rs.propagation_skipped && (rs.retro_applied_at || applied);
        if (skipped) {
          return (
            <div
              className="mt-3 flex items-center justify-between gap-3 px-3 py-2.5 rounded-md"
              style={{
                background: "rgba(249,115,22,0.08)",
                border: "1px solid rgba(249,115,22,0.35)",
              }}
              data-testid={`doctype-history-entry-skipped-${entry.id}`}
            >
              <div className="flex items-start gap-2 min-w-0 flex-1">
                <AlertTriangle size={14} strokeWidth={2.4} style={{ color: "#F97316", marginTop: 2, flexShrink: 0 }} />
                <div className="text-[11.5px]" style={{ color: "#F2F2F2" }}>
                  <strong>Propagation refusée</strong> — {entry.affected_docs_count} document
                  {entry.affected_docs_count > 1 ? "s" : ""} existant
                  {entry.affected_docs_count > 1 ? "s" : ""} n'
                  {entry.affected_docs_count > 1 ? "ont" : "a"} pas reçu cette modification.
                </div>
              </div>
              <button
                type="button"
                disabled={applying}
                onClick={async () => {
                  setApplying(true);
                  try {
                    const { data } = await api.post(
                      `/document-types/${typeId}/propagations/${entry.id}/apply`,
                    );
                    setApplied(true);
                    toast.success("Propagation rattrapée", {
                      description: `${data?.retro_summary_after?.updated_docs || 0} document(s) mis à jour`,
                    });
                    onRetroApplied?.(entry.id, data);
                  } catch (err) {
                    toast.error("Impossible de rattraper", {
                      description: err.response?.data?.detail || "",
                    });
                  } finally {
                    setApplying(false);
                  }
                }}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[11.5px] font-semibold transition-all hover:brightness-110 disabled:opacity-50 shrink-0"
                style={{
                  background: "#0078D4",
                  color: "#FFFFFF",
                  border: "1px solid #0078D4",
                  boxShadow: "0 2px 6px rgba(0,120,212,0.30)",
                }}
                data-testid={`doctype-history-entry-apply-${entry.id}`}
              >
                <RefreshCw size={12} strokeWidth={2.4} className={applying ? "animate-spin" : ""} />
                {applying ? "Application…" : "Appliquer aussi aux existants"}
              </button>
            </div>
          );
        }
        if (alreadyApplied) {
          return (
            <div
              className="mt-3 flex items-center gap-2 px-3 py-2 rounded-md"
              style={{
                background: "rgba(34,197,94,0.06)",
                border: "1px solid rgba(34,197,94,0.30)",
              }}
              data-testid={`doctype-history-entry-applied-${entry.id}`}
            >
              <RefreshCw size={12} strokeWidth={2.4} style={{ color: "#22C55E" }} />
              <span className="text-[11.5px]" style={{ color: "#86EFAC" }}>
                <strong>Propagation rattrapée</strong>
                {rs.retro_applied_at && <> — {new Date(rs.retro_applied_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" })}</>}
                {rs.retro_applied_by && <> · par {rs.retro_applied_by}</>}
              </span>
            </div>
          );
        }
        return null;
      })()}
    </div>
  );
}
